import axios from "axios";
import * as Sentry from "@sentry/react";

import type { FeatureAnnouncementKey } from "../../constants/featureAnnouncements";

import { getOrganization } from "./organizations";
import { getProject } from "./projects";
import { notifyError, notifySuccess } from "./notify";

export const changeUserRole = (user) => async (dispatch) => {
  try {
    await axios.patch("/user/change_role", user);
    dispatch(notifySuccess("Successfully changed user role"));
    dispatch(getOrganization(user.code));
  } catch (err) {
    dispatch(notifyError(`Failed to change user role`));
    Sentry.withScope((scope) => {
      scope.setTag("changedUserMail", user.email);
      scope.setTag("changedUserRole", user.role);
      Sentry.captureException(err);
    });
  }
};

interface UserRole {
  email: string;
  role: string;
}

export const changeUserProjectRole = (user: UserRole, projectId: string) => async (dispatch) => {
  try {
    await axios.patch(`/projects/${projectId}/change_role`, user);
    dispatch(notifySuccess("Successfully changed user role"));
    dispatch(getProject(projectId));
  } catch (err) {
    dispatch(notifyError(`Failed to change user role`));
    Sentry.withScope((scope) => {
      scope.setTag("projectId", projectId);
      scope.setTag("changedUserMail", user.email);
      scope.setTag("changedUserRole", user.role);
      Sentry.captureException(err);
    });
  }
};

export const changeUserAllowGalleries =
  ({ email, isAllowToAccessGallery }: { email: string; isAllowToAccessGallery: boolean }) =>
  async (dispatch) => {
    try {
      await axios.patch("/user/gallery_creator", { email, enabled: isAllowToAccessGallery });
      dispatch(notifySuccess("Successfully changed gallery access"));
      return true;
    } catch (err) {
      dispatch(notifyError(`Failed to change gallery access`));
      Sentry.withScope((scope) => {
        scope.setTag("email", email);
        scope.setTag("isAllowToAccessGallery", isAllowToAccessGallery);
        scope.setExtra("method", "changeUserAllowGalleries");
        Sentry.captureException(err);
      });
      return false;
    }
  };

interface FeatureViewStatus {
  featureKey: FeatureAnnouncementKey;
  version: string;
}

export const getFeatureViews = () => async (): Promise<FeatureViewStatus[]> => {
  try {
    const response = await axios.get<{ views: FeatureViewStatus[] }>("/user/feature-views");
    return response.data.views;
  } catch (err) {
    Sentry.withScope((scope) => {
      scope.setExtra("method", "getFeatureViews");
      Sentry.captureException(err);
    });
    return [];
  }
};

export const markFeatureView = (featureKey: FeatureAnnouncementKey, version: string) => async (): Promise<boolean> => {
  try {
    await axios.post(`/user/feature-views/${encodeURIComponent(featureKey)}`, { version });
    return true;
  } catch (err) {
    Sentry.withScope((scope) => {
      scope.setTag("featureKey", featureKey);
      scope.setTag("featureVersion", version);
      scope.setExtra("method", "markFeatureView");
      Sentry.captureException(err);
    });
    return false;
  }
};
